import { useCallback, useEffect, useRef, useState } from 'react'
import { getSessionAudioUrl } from 'api/noteTakerSessionApi'

const AUDIO_STATUS = {
	IDLE: 'idle',
	LOADING: 'loading',
	READY: 'ready',
	PLAYING: 'playing',
	PAUSED: 'paused',
	ERROR: 'error',
}

function formatTimeMs(ms) {
	const safeMs = Number.isFinite(ms) && ms > 0 ? ms : 0
	const totalSeconds = Math.max(0, Math.floor(safeMs / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function normalizeDurationMs(value, fallback = 0) {
	const parsed = Number(value)
	if (Number.isFinite(parsed) && parsed > 0) {
		return Math.round(parsed)
	}
	const parsedFallback = Number(fallback)
	if (Number.isFinite(parsedFallback) && parsedFallback > 0) {
		return Math.round(parsedFallback)
	}
	return 0
}

export function useSessionAudioPlayer({ sessionNoteId, enabled = true, fallbackDurationMs = 0 }) {
	const audioRef = useRef(null)
	const isSeekingRef = useRef(false)
	const [status, setStatus] = useState(AUDIO_STATUS.IDLE)
	const [audioUrl, setAudioUrl] = useState('')
	const [mimeType, setMimeType] = useState('audio/webm')
	const [currentTimeMs, setCurrentTimeMs] = useState(0)
	const [durationMs, setDurationMs] = useState(() =>
		normalizeDurationMs(fallbackDurationMs),
	)
	const [error, setError] = useState(null)

	const reset = useCallback(() => {
		const audio = audioRef.current
		if (audio) {
			audio.pause()
			audio.removeAttribute('src')
			audio.load()
		}
		audioRef.current = null
		setStatus(AUDIO_STATUS.IDLE)
		setAudioUrl('')
		setMimeType('audio/webm')
		setCurrentTimeMs(0)
		setDurationMs(normalizeDurationMs(fallbackDurationMs))
		setError(null)
	}, [fallbackDurationMs])

	useEffect(() => {
		if (!enabled) {
			reset()
		}
	}, [enabled, reset])

	useEffect(() => {
		setDurationMs(prev => {
			const nextFallback = normalizeDurationMs(fallbackDurationMs)
			if (!nextFallback) return prev
			if (!prev || !Number.isFinite(prev)) return nextFallback
			return prev
		})
	}, [fallbackDurationMs])

	useEffect(() => {
		return () => {
			const audio = audioRef.current
			if (audio) {
				audio.pause()
				audio.removeAttribute('src')
				audio.load()
			}
		}
	}, [])

	const ensureAudioElement = useCallback(
		(url, type = 'audio/webm') => {
			if (!audioRef.current) {
				audioRef.current = new Audio()
				audioRef.current.preload = 'metadata'
				audioRef.current.addEventListener('timeupdate', () => {
					if (isSeekingRef.current) return
					setCurrentTimeMs(
						Math.round((audioRef.current?.currentTime || 0) * 1000),
					)
				})
				audioRef.current.addEventListener('loadedmetadata', () => {
					const rawSeconds = audioRef.current?.duration
					const fromAudio = Number.isFinite(rawSeconds)
						? Math.round(rawSeconds * 1000)
						: 0
					setDurationMs(prev =>
						normalizeDurationMs(fromAudio, prev || fallbackDurationMs),
					)
				})
				audioRef.current.addEventListener('ended', () => {
					setStatus(AUDIO_STATUS.PAUSED)
				})
				audioRef.current.addEventListener('error', () => {
					setStatus(AUDIO_STATUS.ERROR)
					setError({ message: 'Recording unavailable' })
				})
			}

			const audio = audioRef.current
			const nextType = String(type || 'audio/webm').trim() || 'audio/webm'
			if (audio.dataset.sourceUrl === url && audio.dataset.sourceType === nextType) {
				return
			}

			audio.pause()
			while (audio.firstChild) {
				audio.removeChild(audio.firstChild)
			}
			audio.removeAttribute('src')

			const source = document.createElement('source')
			source.src = url
			source.type = nextType
			audio.appendChild(source)
			audio.dataset.sourceUrl = url
			audio.dataset.sourceType = nextType
			audio.load()
		},
		[fallbackDurationMs],
	)

	const loadUrl = useCallback(async () => {
		const noteId = String(sessionNoteId || '').trim()
		if (!noteId) return ''

		setStatus(AUDIO_STATUS.LOADING)
		setError(null)

		try {
			const response = await getSessionAudioUrl(noteId)
			const url = String(response?.url || '')
			if (!url) {
				throw new Error('Audio URL missing')
			}
			const nextMimeType =
				String(response?.mimeType || '').trim() || 'audio/webm'
			setAudioUrl(url)
			setMimeType(nextMimeType)
			setDurationMs(prev =>
				normalizeDurationMs(response?.durationMs, prev || fallbackDurationMs),
			)
			ensureAudioElement(url, nextMimeType)
			setStatus(AUDIO_STATUS.READY)
			return url
		} catch (cause) {
			setStatus(AUDIO_STATUS.ERROR)
			setError({
				code: cause?.name || 'AudioLoadError',
				message: cause?.message || 'Recording unavailable',
			})
			return ''
		}
	}, [ensureAudioElement, fallbackDurationMs, sessionNoteId])

	useEffect(() => {
		if (!enabled || !sessionNoteId) return undefined
		let cancelled = false

		loadUrl().then(url => {
			if (cancelled || !url) return
			audioRef.current?.pause()
			setStatus(AUDIO_STATUS.READY)
		})

		return () => {
			cancelled = true
		}
	}, [enabled, loadUrl, sessionNoteId])

	const togglePlay = useCallback(async () => {
		if (status === AUDIO_STATUS.PLAYING) {
			audioRef.current?.pause()
			setStatus(AUDIO_STATUS.PAUSED)
			return
		}

		let url = audioUrl
		if (!url) {
			url = await loadUrl()
			if (!url) return
		} else {
			ensureAudioElement(url, mimeType)
		}

		try {
			await audioRef.current?.play()
			setStatus(AUDIO_STATUS.PLAYING)
		} catch (cause) {
			setStatus(AUDIO_STATUS.ERROR)
			setError({
				code: cause?.name || 'AudioPlayError',
				message: cause?.message || 'Could not play recording',
			})
		}
	}, [audioUrl, ensureAudioElement, loadUrl, mimeType, status])

	const seek = useCallback(
		ms => {
			const safeDuration = normalizeDurationMs(durationMs, fallbackDurationMs)
			if (!audioRef.current || !safeDuration) return

			const nextMs = Math.max(0, Math.min(ms, safeDuration))
			const nextSeconds = nextMs / 1000

			isSeekingRef.current = true
			audioRef.current.currentTime = nextSeconds
			setCurrentTimeMs(Math.round(nextSeconds * 1000))
			window.requestAnimationFrame(() => {
				isSeekingRef.current = false
			})
		},
		[durationMs, fallbackDurationMs],
	)

	const retry = useCallback(() => {
		setAudioUrl('')
		return loadUrl()
	}, [loadUrl])

	const safeDurationMs = normalizeDurationMs(durationMs, fallbackDurationMs)

	return {
		status,
		audioUrl,
		currentTimeMs,
		durationMs: safeDurationMs,
		error,
		isPlaying: status === AUDIO_STATUS.PLAYING,
		isLoading: status === AUDIO_STATUS.LOADING,
		togglePlay,
		seek,
		retry,
		reset,
		formatTimeMs,
	}
}
